// ------------------------------------
// props 輸入需求 :
// props.data : object : { data: array }            (  )
// ------------------------------------

import React, { useEffect, useState } from 'react';
import UiIntegrater from '../littleUIComponent/UiIntegrater';
import VitalChart from './VitalCharts';
import ApiGo from '../../../../actions/ApiGo';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TimeSelector from './TimeSelector';
import ConfigHandler from '../../../../actions/ConfigHandler';
import { ADD_RESPONSE, ADD_RESPONSE_JSON } from '../../../../stores/reducer_add';

const TAIPEI_TIME_ZONE = 'Asia/Taipei';
const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000;

const toDate = (value) => {
    if (value instanceof Date) return value;

    if (value === null || value === undefined || value === '') {
        return new Date(NaN);
    }

    if (typeof value === 'number') {
        return new Date(value);
    }

    if (typeof value === 'string') {
        const text = value.trim();

        // 1) 支援純數字 timestamp
        if (/^\d+$/.test(text)) {
            return new Date(Number(text));
        }

        // 2) 支援沒有時區的格式：YYYY-MM-DD HH:mm 或 YYYY-MM-DD HH:mm:ss
        const match = text.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
        );

        if (match) {
            const [, y, m, d, hh = '00', mm = '00', ss = '00'] = match;

            // 把「台灣日期字串」轉成 UTC timestamp
            const utcMs =
                Date.UTC(
                    Number(y),
                    Number(m) - 1,
                    Number(d),
                    Number(hh),
                    Number(mm),
                    Number(ss)
                ) - TAIPEI_OFFSET_MS;

            return new Date(utcMs);
        }
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date(NaN) : parsed;
};

const formatTaipeiDateTime = (value, withSeconds = true) => {
    const date = toDate(value);
    if (Number.isNaN(date.getTime())) return '--';

    const options = {
        timeZone: TAIPEI_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        hourCycle: 'h23',
    };

    if (withSeconds) {
        options.second = '2-digit';
    }

    const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(date);
    const map = {};
    parts.forEach((part) => {
        if (part.type !== 'literal') {
            map[part.type] = part.value;
        }
    });

    const year = map.year || '0000';
    const month = map.month || '00';
    const day = map.day || '00';
    const hour = map.hour || '00';
    const minute = map.minute || '00';
    const second = map.second || '00';

    return withSeconds
        ? `${year}-${month}-${day} ${hour}:${minute}:${second}`
        : `${year}-${month}-${day} ${hour}:${minute}`;
};

const HISVital = (props) => {
    const config_type = ConfigHandler.isShowCF();
    const { t } = useTranslation();

    // --- redux ------
    const store_timestamp = useSelector((state) => state.store_historyData.timestamp);
    const store_start = useSelector((state) => state.store_historyData.timestampBegin);
    const store_end = useSelector((state) => state.store_historyData.timestampEnd);
    const dispatch = useDispatch();
    // --- redux ------

    const residentId = props?.data?.id;

    const [date, setDate] = useState(0);
    const [timeSelector, setTimeSelector] = useState(false);
    const [breathRow, setBreathRow] = useState([]);
    const [heartRow, setHeartRow] = useState([]);
    const [reset, setReset] = useState(false);
    const [start, setStart] = useState(store_start);
    const [end, setEnd] = useState(store_end);

    const [yAxis, setyAxis] = useState(parseInt(localStorage.getItem('y-axis'), 10) || 40);
    const [changeY, setChangeY] = useState(true);
    const [timeInterval, setTimeInterval] = useState(['', '']);

    function toggoleTimeSelctor() {
        setTimeSelector(!timeSelector);
    }

    function toggleY() {
        setChangeY(!changeY);
        localStorage.setItem('y-axis', yAxis);
    }

    function toggleReset(timeStart, timeEnd) {
        setReset(true);
        setStart(timeStart);
        setEnd(timeEnd);
    }

    const loadVitalData = async (timeStart, timeEnd) => {
        if (!residentId) return;

        setBreathRow([]);
        setHeartRow([]);

        try {
            const alertStart = formatTaipeiDateTime(timeStart, true);
            const alertEnd = formatTaipeiDateTime(timeEnd, true);

            const res = await ApiGo.getAlert_for_vital(residentId, alertStart, alertEnd);
            const alerts = Array.isArray(res?.alerts) ? [...res.alerts] : [];

            if (alerts.length === 0) {
                return;
            }

            if (alerts[0]?.bedState === 1) {
                alerts.shift();
            }

            if (alerts.length === 0) {
                return;
            }

            const segments = [];

            for (let i = 0; i + 1 < alerts.length; i += 2) {
                const from = alerts[i]?.timestamp;
                const to = alerts[i + 1]?.timestamp;

                if (from && to) {
                    segments.push([from, to]);
                }
            }

            const lastTimestamp = alerts[alerts.length - 1]?.timestamp;
            if (lastTimestamp) {
                segments.push([
                    lastTimestamp,
                    formatTaipeiDateTime(Date.now(), true),
                ]);
            }

            const curveResults = await Promise.all(
                segments.map(async ([from, to]) => {
                    const curve = await ApiGo.getVital_Curve(residentId, from, to);

                    const hr = [];
                    const rr = [];

                    if (Array.isArray(curve)) {
                        curve.forEach((item) => {
                            if (item?.type === 'hr') hr.push(item);
                            if (item?.type === 'rr') rr.push(item);
                        });
                    }

                    const cleanedRR = rr.length >= 2 ? rr.slice(1, -1) : [];

                    return {
                        hr,
                        rr: cleanedRR,
                    };
                })
            );

            setBreathRow(curveResults.map((item) => item.rr));
            setHeartRow(curveResults.map((item) => item.hr));
        } catch (err) {
            dispatch(
                ADD_RESPONSE({
                    status: err?.status,
                    statusText: err?.statusText,
                    type: 'hisRec_no_data',
                })
            );
            dispatch(ADD_RESPONSE_JSON(err?.response));
        }
    };

    useEffect(() => {
        if (store_timestamp !== 0 && store_start !== 0 && store_end !== 0) {
            setDate(store_timestamp);
            setStart(store_start);
            setEnd(store_end);

            if (!reset) {
                loadVitalData(store_start, store_end);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store_timestamp, store_start, store_end, reset, residentId]);

    useEffect(() => {
        if (!reset) return;

        loadVitalData(timeInterval[0], timeInterval[1]);
        setReset(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset, timeInterval, residentId]);

    const type_RR = {
        title: 'HISTORY_RECORD.BREATH_HIS',
        ma: 'HISTORY_RECORD.MA',
        avg: 'HISTORY_RECORD.AVG_BREATH_COUNT',
        backgroundColor: 'none',
        high: props?.data?.breath_high,
        low: props?.data?.breath_low,
        type: 'RR',
    };

    const type_HR = {
        title: 'HISTORY_RECORD.HEART_HIS',
        ma: 'HISTORY_RECORD.MA',
        avg: 'HISTORY_RECORD.AVG_HEART_COUNT',
        backgroundColor: 'none',
        high: props?.data?.heart_high,
        low: props?.data?.heart_low,
        type: 'HR',
    };

    return (
        <>
            <UiIntegrater
                OnOff_switch={[true, false, false, true, true]}
                date={new Date()}
                name={props?.data !== undefined ? props.data.last_name : ''}
                bed={props?.data !== undefined ? props.data.first_name : ''}
            />

            <div className="fx-d-c h-90">
                <div
                    className="fx-d-r fx-fsc timeslecMobile h-5 timePresent frame"
                    onClick={toggoleTimeSelctor}
                >
                    <div className="br-10 time_pink fx-ccc w-20 cursor pt h-100">
                        {t('HISTORY_RECORD.TIME_SELECT')}
                    </div>

                    {timeSelector ? (
                        <TimeSelector
                            hide={toggoleTimeSelctor}
                            toggleReset={toggleReset}
                            setTimeInterval={setTimeInterval}
                        />
                    ) : null}

                    <div className="timeslecMobile fx-d-r fx-cspar content">
                        {t('HISTORY_RECORD.CHOSEN_TIME')} &nbsp; : &nbsp;
                        {formatTaipeiDateTime(start, false)}
                        &nbsp;&nbsp; - &nbsp;&nbsp;
                        {formatTaipeiDateTime(end, false)}
                    </div>
                </div>

                {config_type ? (
                    <VitalChart
                        yAxis={{ y: yAxis, change: changeY }}
                        breathData={breathRow}
                        timeInterval={timeInterval}
                        date={date}
                        residentSetting={props.data}
                        type={type_RR}
                    />
                ) : (
                    <VitalChart
                        yAxis={{ y: yAxis, change: changeY }}
                        breathData={breathRow}
                        timeInterval={[store_start, store_end]}
                        date={date}
                        residentSetting={props.data}
                        type={type_RR}
                    />
                )}

                {config_type ? (
                    <VitalChart
                        yAxis={{ y: yAxis, change: changeY }}
                        breathData={heartRow}
                        timeInterval={timeInterval}
                        date={date}
                        residentSetting={props.data}
                        type={type_HR}
                    />
                ) : null}

                <div className="fx-d-r vitalYaxis">
                    <div>{t('HISTORY_RECORD.Y_AXIS_ADJUST')}：</div>
                    <input
                        className="w-10 yAxisInput timer_inputBox"
                        type="number"
                        value={yAxis}
                        onChange={(e) => setyAxis(e.target.value)}
                    />
                    <div className="fx-ccc confirmBtm" onClick={toggleY}>
                        {t('GLOBAL.CONFIRM')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default HISVital;